import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const UsersManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  
  // New user dialog
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'admin'
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get('/api/users');
        setUsers(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Ошибка при загрузке пользователей');
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle role filter change
  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
    setPage(0);
  };

  // Filter users based on search query and role filter
  const filteredUsers = users.filter((user) => {
    const searchStr = searchQuery.toLowerCase();
    const matchesSearch = 
      user.name.toLowerCase().includes(searchStr) ||
      user.email.toLowerCase().includes(searchStr) ||
      (user.phone && user.phone.toLowerCase().includes(searchStr));
    
    const matchesRole = roleFilter === '' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Calculate pagination
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Open delete dialog
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Handle user deletion
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      await axios.delete(`/api/users/${userToDelete.id}`);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setDeleteLoading(false);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Ошибка при удалении пользователя');
      setDeleteLoading(false);
    }
  };

  // Open role update dialog
  const handleOpenRoleDialog = (user) => {
    setUserToUpdate(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  // Close role update dialog
  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setUserToUpdate(null);
    setNewRole('');
  };

  // Handle role change
  const handleRoleChange = (event) => {
    setNewRole(event.target.value);
  };

  // Handle user role update
  const handleUpdateUserRole = async () => {
    if (!userToUpdate || !newRole) return;

    try {
      setUpdateLoading(true);
      await axios.put(`/api/users/${userToUpdate.id}`, { role: newRole });
      setUsers(users.map(u => 
        u.id === userToUpdate.id ? { ...u, role: newRole } : u
      ));
      setUpdateLoading(false);
      handleCloseRoleDialog();
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Ошибка при обновлении роли пользователя');
      setUpdateLoading(false);
    }
  };

  // Handle new user form change
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserData({
      ...newUserData,
      [name]: value
    });
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Handle create new admin user
  const handleCreateUser = async () => {
    // Validate form
    const errors = {};
    if (!newUserData.name) errors.name = 'Имя обязательно';
    if (!newUserData.email) errors.email = 'Email обязателен';
    if (!/^\S+@\S+\.\S+$/.test(newUserData.email)) errors.email = 'Некорректный email';
    if (!newUserData.password) errors.password = 'Пароль обязателен';
    if (newUserData.password.length < 6) errors.password = 'Пароль должен быть не менее 6 символов';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      setCreateLoading(true);
      const { data } = await axios.post('/api/users', newUserData);
      setUsers([...users, data]);
      setCreateLoading(false);
      setNewUserDialogOpen(false);
      setNewUserData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'admin'
      });
      setFormErrors({});
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Ошибка при создании пользователя');
      setCreateLoading(false);
      if (error.response && error.response.data) {
        if (error.response.data.message === 'User already exists') {
          setFormErrors({...formErrors, email: 'Пользователь с таким email уже существует'});
        }
      }
    }
  };

  // Get role chip color and label
  const getRoleChip = (role) => {
    switch (role) {
      case 'superadmin':
        return <Chip label="Суперадмин" color="error" size="small" />;
      case 'admin':
        return <Chip label="Администратор" color="primary" size="small" />;
      case 'client':
        return <Chip label="Клиент" color="default" size="small" />;
      default:
        return <Chip label={role} size="small" />;
    }
  };

  // Get user avatar
  const getUserAvatar = (user) => {
    return (
      <Avatar sx={{ bgcolor: stringToColor(user.name), width: isMobile ? 28 : 40, height: isMobile ? 28 : 40 }}>
        {user.name.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  // Generate color from string
  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
  };

  // Check if current user can edit the target user
  const canEditUser = (targetUser) => {
    // Superadmin can edit anyone except themselves
    if (currentUser.role === 'superadmin' && currentUser.id !== targetUser.id) {
      return true;
    }
    
    // Admin can edit clients only
    if (currentUser.role === 'admin' && targetUser.role === 'client') {
      return true;
    }
    
    return false;
  };

  // Check if current user can delete the target user
  const canDeleteUser = (targetUser) => {
    // Same rules as editing
    return canEditUser(targetUser);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: { xs: 2, sm: 3 } 
      }}>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Пользователи
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={isMobile ? "small" : "medium"}
          startIcon={<PersonAddIcon />}
          onClick={() => setNewUserDialogOpen(true)}
          disabled={currentUser?.role !== 'superadmin'}
        >
          {isMobile ? "Добавить" : "Добавить администратора"}
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: { xs: 2, sm: 3 }, borderRadius: { xs: 1, sm: 2 } }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 }
          }}>
            <TextField
              variant="outlined"
              placeholder="Поиск по имени, email, телефону..."
              value={searchQuery}
              onChange={handleSearchChange}
              size={isMobile ? "small" : "medium"}
              sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size={isMobile ? "small" : "medium"}>
              <InputLabel id="role-filter-label">Роль</InputLabel>
              <Select
                labelId="role-filter-label"
                value={roleFilter}
                label="Роль"
                onChange={handleRoleFilterChange}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="client">Клиент</MenuItem>
                <MenuItem value="admin">Администратор</MenuItem>
                <MenuItem value="superadmin">Суперадмин</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ 
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.light,
                borderRadius: 4
              }
            }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}></TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Имя</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Email</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Телефон</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Роль</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Дата регистрации</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Последний вход</TableCell>
                    <TableCell align="right" sx={{ p: { xs: 1, sm: 2 } }}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>{getUserAvatar(user)}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 80, sm: 150 } }}>
                            {user.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 200 } }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>{user.phone || '-'}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>{getRoleChip(user.role)}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : '-'}</TableCell>
                        <TableCell align="right" sx={{ p: { xs: 1, sm: 2 } }}>
                          {canEditUser(user) && (
                            <IconButton 
                              color="primary"
                              onClick={() => handleOpenRoleDialog(user)}
                              size={isMobile ? "small" : "medium"}
                            >
                              <EditIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                          )}
                          {canDeleteUser(user) && (
                            <IconButton 
                              color="error"
                              onClick={() => handleOpenDeleteDialog(user)}
                              size={isMobile ? "small" : "medium"}
                            >
                              <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {searchQuery || roleFilter
                          ? 'Нет пользователей, соответствующих фильтрам' 
                          : 'Нет пользователей'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={isMobile ? "Строк:" : "Строк на странице:"}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Удаление пользователя</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить пользователя {userToDelete?.name}? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Update Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={handleCloseRoleDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Изменение роли пользователя</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Выберите новую роль для пользователя {userToUpdate?.name}
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="new-role-label">Роль</InputLabel>
            <Select
              labelId="new-role-label"
              value={newRole}
              label="Роль"
              onChange={handleRoleChange}
            >
              <MenuItem value="client">Клиент</MenuItem>
              <MenuItem value="admin">Администратор</MenuItem>
              {currentUser?.role === 'superadmin' && (
                <MenuItem value="superadmin">Суперадмин</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleDialog} disabled={updateLoading}>
            Отмена
          </Button>
          <Button 
            onClick={handleUpdateUserRole} 
            color="primary" 
            variant="contained"
            disabled={updateLoading || newRole === userToUpdate?.role}
            startIcon={updateLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {updateLoading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* New User Dialog */}
      <Dialog 
        open={newUserDialogOpen} 
        onClose={() => !createLoading && setNewUserDialogOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Создание нового администратора</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Введите информацию для нового администратора
          </DialogContentText>
          <Box component="form" sx={{ mt: 1 }} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Имя"
              name="name"
              autoComplete="name"
              value={newUserData.name}
              onChange={handleNewUserChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={createLoading}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              value={newUserData.email}
              onChange={handleNewUserChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={createLoading}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Пароль"
              type="password"
              id="password"
              value={newUserData.password}
              onChange={handleNewUserChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              disabled={createLoading}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              fullWidth
              name="phone"
              label="Телефон"
              id="phone"
              value={newUserData.phone}
              onChange={handleNewUserChange}
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              disabled={createLoading}
              size={isMobile ? "small" : "medium"}
            />
            <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
              <InputLabel id="role-label">Роль</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={newUserData.role}
                label="Роль"
                onChange={handleNewUserChange}
                disabled={createLoading}
              >
                <MenuItem value="admin">Администратор</MenuItem>
                {currentUser?.role === 'superadmin' && (
                  <MenuItem value="superadmin">Суперадмин</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNewUserDialogOpen(false)} 
            disabled={createLoading}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleCreateUser} 
            color="primary" 
            variant="contained"
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {createLoading ? 'Создание...' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersManagement;
